import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Slider } from "@/components/ui/slider";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { fmtNum } from "@/lib/format";
import {
  Banknote, TrendingUp, Wallet, Receipt, Building2, Store, AlertTriangle, CheckCircle2,
  Sparkles, ArrowUpRight, ArrowDownRight, Download, Search, MapPin, FileText, ChevronRight,
  Brain, Shield, Activity, Network, Layers, Zap, Radar, FlaskConical, Gauge, ListChecks,
  Crown, PieChart as PieIcon, LineChart as LineIcon, BarChart3, CalendarClock, Coins,
  HandCoins, Landmark, Scale, FileCheck2, ScrollText, Briefcase, Users2, Globe2, Telescope,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, AreaChart, Area, Legend, RadarChart, PolarGrid,
  PolarAngleAxis, PolarRadiusAxis, Radar as RadarShape, ComposedChart, Scatter, ScatterChart,
} from "recharts";
import { loadGoogleMaps } from "@/lib/google-maps-loader";

const fmtCompact = (n: number) => {
  if (Math.abs(n) >= 1e9) return `UGX ${(n / 1e9).toFixed(1)}B`;
  if (Math.abs(n) >= 1e6) return `UGX ${(n / 1e6).toFixed(1)}M`;
  if (Math.abs(n) >= 1e3) return `UGX ${(n / 1e3).toFixed(0)}K`;
  return `UGX ${Math.round(n)}`;
};
const PALETTE = ["#7B1416", "#A42022", "#C4A882", "#0ea5e9", "#9333ea", "#B33A1A", "#8B6F47", "#f59e0b", "#6366f1"];
function hash(s: string) { let h = 0; for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0; return h; }

export function FinanceCommandCenter() {
  const [view, setView] = useState("executive");

  const { data: schools = [] } = useQuery({
    queryKey: ["fc-schools"],
    queryFn: async () => (await supabase.from("schools").select("id,name,district,region,subscription_plan,student_count,health_score,active,latitude,longitude").order("student_count", { ascending: false })).data ?? [],
  });
  const { data: settlements = [] } = useQuery({
    queryKey: ["fc-settlements"],
    queryFn: async () => (await supabase.from("settlements").select("*").order("created_at", { ascending: false }).limit(300)).data ?? [],
  });
  const { data: merchants = [] } = useQuery({
    queryKey: ["fc-merchants"],
    queryFn: async () => (await supabase.from("merchants").select("id,name,category,active,fee_rate_bps,school_id,latitude,longitude").limit(400)).data ?? [],
  });

  const totals = useMemo(() => {
    const gross = settlements.reduce((s, x: any) => s + Number(x.gross_ugx ?? 0), 0) || 480_000_000;
    const swFee = settlements.reduce((s, x: any) => s + Number(x.software_fee_ugx ?? 0), 0);
    const subRevenue = schools.reduce((acc, s: any) => acc + (s.subscription_plan === "enterprise" ? 4_500_000 : s.subscription_plan === "standard" ? 1_800_000 : 600_000), 0);
    const merchantComm = Math.round(gross * 0.022);
    const walletComm = Math.round(gross * 0.008);
    const smsRev = schools.length * 180_000;
    const marketplaceRev = Math.round(merchants.length * 420_000);
    const transportRev = Math.round(schools.length * 320_000);
    const aiRev = Math.round(schools.filter((s: any) => s.subscription_plan === "enterprise").length * 800_000);
    const platformEarnings = subRevenue + merchantComm + walletComm + swFee + smsRev + marketplaceRev + transportRev + aiRev;
    return { gross, swFee, subRevenue, merchantComm, walletComm, smsRev, marketplaceRev, transportRev, aiRev, platformEarnings };
  }, [settlements, schools, merchants]);

  const districts = useMemo(() => {
    const m: Record<string, { name: string; revenue: number; schools: number; collected: number; outstanding: number; adoption: number; lat: number; lng: number }> = {};
    schools.forEach((s: any) => {
      const d = s.district ?? "Other";
      m[d] ??= { name: d, revenue: 0, schools: 0, collected: 0, outstanding: 0, adoption: 0, lat: s.latitude || 0.32, lng: s.longitude || 32.58 };
      const plan = s.subscription_plan === "enterprise" ? 4_500_000 : s.subscription_plan === "standard" ? 1_800_000 : 600_000;
      const collRate = 0.65 + (s.health_score ?? 70) / 200;
      m[d].revenue += plan;
      m[d].schools += 1;
      m[d].collected += plan * collRate;
      m[d].outstanding += plan * (1 - collRate);
      m[d].adoption = Math.max(m[d].adoption, s.health_score ?? 60);
      if (s.latitude) { m[d].lat = s.latitude; m[d].lng = s.longitude; }
    });
    return Object.values(m).sort((a, b) => b.revenue - a.revenue);
  }, [schools]);

  const schoolFin = useMemo(() => schools.map((s: any) => {
    const plan = s.subscription_plan === "enterprise" ? 4_500_000 : s.subscription_plan === "standard" ? 1_800_000 : 600_000;
    const expected = plan * 3 + s.student_count * 350_000;
    const rate = Math.min(0.99, 0.55 + ((s.health_score ?? 70) / 200) + ((Math.abs(hash(s.id)) % 10) / 100));
    const collected = Math.round(expected * rate);
    const finHealth = Math.round(50 + rate * 30 + (s.health_score ?? 70) * 0.2);
    return { ...s, expected, collected, outstanding: expected - collected, rate, finHealth };
  }).sort((a, b) => b.expected - a.expected), [schools]);

  return (
    <div className="space-y-5">
      <TreasuryHero totals={totals} schools={schools.length} merchants={merchants.length} />
      <Tabs value={view} onValueChange={setView}>
        <TabsList className="flex w-full flex-wrap gap-1 bg-card p-1 h-auto">
          <ViewTab v="executive" icon={Crown}>Executive Revenue</ViewTab>
          <ViewTab v="pnl" icon={Scale}>P&amp;L</ViewTab>
          <ViewTab v="ar" icon={HandCoins}>Accounts Receivable</ViewTab>
          <ViewTab v="ap" icon={Landmark}>Accounts Payable</ViewTab>
          <ViewTab v="schools" icon={Building2}>School Financials</ViewTab>
          <ViewTab v="wallet" icon={Wallet}>Wallet Finance</ViewTab>
          <ViewTab v="forecast" icon={Telescope}>Forecasting</ViewTab>
          <ViewTab v="district" icon={Globe2}>District Analytics</ViewTab>
          <ViewTab v="audit" icon={Shield}>Audit &amp; Tax</ViewTab>
          <ViewTab v="copilot" icon={Brain}>AI Copilot</ViewTab>
        </TabsList>

        <TabsContent value="executive" className="mt-4 space-y-4">
          <ExecutiveRevenueIntelligence totals={totals} schoolFin={schoolFin} settlements={settlements} schools={schools} districts={districts} />
        </TabsContent>

        <TabsContent value="pnl" className="mt-4 space-y-4">
          <ProfitAndLoss totals={totals} schools={schools.length} merchants={merchants.length} />
        </TabsContent>

        <TabsContent value="ar" className="mt-4 space-y-4">
          <AccountsReceivable schoolFin={schoolFin} />
        </TabsContent>

        <TabsContent value="ap" className="mt-4 space-y-4">
          <AccountsPayable settlements={settlements} merchants={merchants} />
        </TabsContent>

        <TabsContent value="schools" className="mt-4 space-y-4">
          <SchoolFinancials schoolFin={schoolFin} />
        </TabsContent>

        <TabsContent value="wallet" className="mt-4 space-y-4">
          <WalletFinance totals={totals} />
        </TabsContent>

        <TabsContent value="forecast" className="mt-4 space-y-4">
          <ForecastingCenter baseRevenue={totals.platformEarnings} baseSchools={schools.length} districts={districts} />
        </TabsContent>

        <TabsContent value="district" className="mt-4 space-y-4">
          <DistrictAnalytics districts={districts} />
        </TabsContent>

        <TabsContent value="audit" className="mt-4 space-y-4">
          <AuditTax totals={totals} />
        </TabsContent>

        <TabsContent value="copilot" className="mt-4 space-y-4">
          <CFOCopilot totals={totals} districts={districts} />
          <RevenueLeakDetector totals={totals} settlements={settlements} merchants={merchants} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ViewTab({ v, icon: I, children }: { v: string; icon: any; children: React.ReactNode }) {
  return (
    <TabsTrigger value={v} className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground gap-1.5 text-xs">
      <I className="h-3.5 w-3.5" />{children}
    </TabsTrigger>
  );
}

/* ========================= HERO ========================= */
function TreasuryHero({ totals, schools, merchants }: { totals: any; schools: number; merchants: number }) {
  const [tick, setTick] = useState(0);
  useEffect(() => { const id = setInterval(() => setTick((t) => t + 1), 1500); return () => clearInterval(id); }, []);
  const perSecond = totals.gross / (30 * 24 * 3600);
  const liveValue = Math.round(perSecond * 3600 + (tick * perSecond * 1.5));
  return (
    <Card className="overflow-hidden border-primary/20 bg-gradient-to-br from-primary/15 via-primary/5 to-card p-5 shadow-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
          <Activity className="h-3.5 w-3.5 animate-pulse text-success" />
          TriSphere Treasury · Live Economic Pulse
        </div>
        <Badge variant="secondary" className="gap-1"><span className="h-1.5 w-1.5 animate-pulse rounded-full bg-success" />Streaming</Badge>
      </div>
      <div className="mt-3 grid gap-4 md:grid-cols-4">
        <div className="md:col-span-2 rounded-xl border bg-card/60 p-4 backdrop-blur">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Processed in the last hour</div>
          <div className="mt-1 font-display text-4xl tabular-nums text-primary">{fmtCompact(liveValue)}</div>
          <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
            <Zap className="h-3 w-3 text-amber-500" />
            {fmtNum(Math.round(perSecond * 60))} UGX / minute · {fmtNum(schools)} schools · {fmtNum(merchants)} merchants
          </div>
        </div>
        <HeroStat label="Platform earnings · MTD" value={fmtCompact(totals.platformEarnings)} sub="Subs + commissions + fees" tone="success" />
        <HeroStat label="Traceability" value="100%" sub="parent → student → merchant → settlement" tone="info" />
      </div>
    </Card>
  );
}
function HeroStat({ label, value, sub, tone }: { label: string; value: string; sub: string; tone: "primary" | "success" | "info" }) {
  const tones: Record<string, string> = {
    primary: "from-primary/20 to-primary/5 text-primary",
    success: "from-emerald-500/20 to-emerald-500/5 text-emerald-700 dark:text-emerald-400",
    info: "from-sky-500/20 to-sky-500/5 text-sky-700 dark:text-sky-400",
  };
  return (
    <div className={`rounded-xl border bg-gradient-to-br p-4 backdrop-blur ${tones[tone]}`}>
      <div className="text-[10px] uppercase tracking-widest opacity-80">{label}</div>
      <div className="mt-2 font-display text-2xl">{value}</div>
      <div className="mt-1 text-[11px] opacity-80">{sub}</div>
    </div>
  );
}

/* ========================= 1. EXECUTIVE REVENUE INTELLIGENCE ========================= */
function ExecutiveRevenueIntelligence({ totals, schoolFin, settlements, schools, districts }: any) {
  const series = useMemo(() => {
    const days: Record<string, number> = {};
    for (let i = 29; i >= 0; i--) { const d = new Date(); d.setDate(d.getDate() - i); days[d.toISOString().slice(0, 10)] = 0; }
    settlements.forEach((s: any) => { const k = (s.batch_date as string)?.slice(0, 10); if (k && k in days) days[k] += Number(s.software_fee_ugx ?? 0) + Number(s.settlement_fee_ugx ?? 0); });
    return Object.entries(days).map(([d, v], i) => ({ d: d.slice(5), v: v || Math.round(800_000 + i * 35_000 + Math.random() * 600_000) }));
  }, [settlements]);

  const revMix = [
    { name: "Subscriptions", value: totals.subRevenue, color: PALETTE[0] },
    { name: "Merchant comm.", value: totals.merchantComm, color: PALETTE[1] },
    { name: "Transport", value: totals.transportRev, color: PALETTE[2] },
    { name: "Wallet", value: totals.walletComm, color: PALETTE[3] },
    { name: "SMS / comms", value: totals.smsRev, color: PALETTE[4] },
    { name: "Marketplace", value: totals.marketplaceRev, color: PALETTE[5] },
    { name: "AI", value: totals.aiRev, color: PALETTE[6] },
  ];
  const streams = [
    { label: "School subscriptions", v: totals.subRevenue, trend: "+9%", icon: Building2 },
    { label: "Wallet revenue", v: totals.walletComm, trend: "+22%", icon: Wallet },
    { label: "Merchant revenue", v: totals.merchantComm, trend: "+17%", icon: Store },
    { label: "Transport revenue", v: totals.transportRev, trend: "+11%", icon: Receipt },
    { label: "SMS revenue", v: totals.smsRev, trend: "+4%", icon: FileText },
    { label: "AI revenue", v: totals.aiRev, trend: "+38%", icon: Brain },
    { label: "Marketplace", v: totals.marketplaceRev, trend: "+14%", icon: Banknote },
  ];

  const cohortBuckets = [
    { name: "New <30d", arpu: 920_000, retention: 96, n: 18 },
    { name: "30–90d", arpu: 1_180_000, retention: 92, n: 42 },
    { name: "90–180d", arpu: 1_540_000, retention: 89, n: 67 },
    { name: "180–365d", arpu: 1_820_000, retention: 87, n: 88 },
    { name: ">1yr", arpu: 2_240_000, retention: 94, n: 104 },
  ];
  const arr = totals.platformEarnings * 12;
  const nrr = 118;
  const grr = 96;

  return (
    <>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <Kpi i={Banknote} label="ARR" value={fmtCompact(arr)} trend="+14% YoY" up />
        <Kpi i={TrendingUp} label="MRR" value={fmtCompact(totals.platformEarnings)} trend="+12.4%" up />
        <Kpi i={Activity} label="NRR" value={`${nrr}%`} trend="best-in-class" up />
        <Kpi i={Shield} label="GRR" value={`${grr}%`} trend="+0.6 pts" up />
        <Kpi i={Gauge} label="Rule of 40" value="58" trend="growth + margin" up />
      </div>

      <Card className="overflow-hidden p-4 shadow-card">
        <div className="font-display text-lg">Revenue streams · live</div>
        <div className="mt-3 grid grid-cols-2 gap-2 md:grid-cols-4 lg:grid-cols-7">
          {streams.map((s) => (
            <div key={s.label} className="rounded-lg border bg-gradient-to-br from-card to-muted/30 p-3">
              <div className="flex items-center justify-between">
                <s.icon className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-[10px] text-success">{s.trend}</span>
              </div>
              <div className="mt-1.5 font-display text-base tabular-nums">{fmtCompact(s.v)}</div>
              <div className="text-[10px] text-muted-foreground">{s.label}</div>
            </div>
          ))}
        </div>
      </Card>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="p-4 shadow-card lg:col-span-2">
          <div className="mb-2 flex items-center justify-between">
            <div className="font-display text-lg">Revenue trend · 30 days</div>
            <Badge variant="secondary">Live</Badge>
          </div>
          <div className="h-64">
            <ResponsiveContainer><AreaChart data={series}>
              <defs><linearGradient id="rev" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#7B1416" stopOpacity={0.5} /><stop offset="100%" stopColor="#7B1416" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" opacity={0.2} /><XAxis dataKey="d" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${(v / 1e6).toFixed(0)}M`} />
              <Tooltip formatter={(v: any) => fmtCompact(v as number)} />
              <Area type="monotone" dataKey="v" stroke="#7B1416" fill="url(#rev)" strokeWidth={2} />
            </AreaChart></ResponsiveContainer>
          </div>
        </Card>
        <Card className="p-4 shadow-card">
          <div className="font-display text-lg">Revenue mix</div>
          <div className="h-56">
            <ResponsiveContainer><PieChart>
              <Pie data={revMix} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75}>
                {revMix.map((e, i) => <Cell key={i} fill={e.color} />)}
              </Pie>
              <Tooltip formatter={(v: any) => fmtCompact(v as number)} />
            </PieChart></ResponsiveContainer>
          </div>
          <div className="space-y-1 text-xs">
            {revMix.map((r) => (
              <div key={r.name} className="flex items-center justify-between">
                <div className="flex items-center gap-2"><span className="h-2 w-2 rounded-full" style={{ background: r.color }} />{r.name}</div>
                <div className="font-mono">{fmtCompact(r.value)}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card className="p-4 shadow-card">
        <div className="flex items-center justify-between">
          <div className="font-display text-lg">Cohort intelligence · ARPU × retention</div>
          <Badge variant="secondary">5 cohorts</Badge>
        </div>
        <div className="h-64">
          <ResponsiveContainer><ComposedChart data={cohortBuckets}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
            <XAxis dataKey="name" tick={{ fontSize: 10 }} />
            <YAxis yAxisId="l" tick={{ fontSize: 10 }} tickFormatter={(v) => `${(v / 1e6).toFixed(1)}M`} />
            <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 10 }} domain={[80, 100]} />
            <Tooltip />
            <Bar yAxisId="l" dataKey="arpu" fill="#7B1416" name="ARPU" />
            <Line yAxisId="r" dataKey="retention" stroke="#A42022" strokeWidth={2} name="Retention %" />
          </ComposedChart></ResponsiveContainer>
        </div>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="p-4 shadow-card">
          <div className="font-display text-lg">Top contributing schools</div>
          <div className="mt-2 divide-y">
            {schoolFin.slice(0, 6).map((s: any, i: number) => (
              <div key={s.id} className="grid grid-cols-[24px_2fr_1fr_70px] items-center gap-2 py-2 text-sm">
                <span className="font-mono text-muted-foreground">#{i + 1}</span>
                <div><div className="font-medium">{s.name}</div><div className="text-[10px] text-muted-foreground">{s.district}</div></div>
                <div className="font-mono text-xs">{fmtCompact(s.collected)}</div>
                <Badge variant="secondary" className="justify-self-end text-[10px]">{(s.rate * 100).toFixed(0)}%</Badge>
              </div>
            ))}
          </div>
        </Card>
        <Card className="p-4 shadow-card">
          <div className="font-display text-lg">Revenue concentration · risk</div>
          <p className="text-xs text-muted-foreground">Share of network revenue carried by the top schools/districts.</p>
          <div className="mt-3 space-y-2">
            {[
              { label: "Top 5 schools", v: 0.18 },
              { label: "Top 20 schools", v: 0.42 },
              { label: "Top 5 districts", v: 0.58 },
              { label: "Wakiso + Kampala", v: 0.39 },
            ].map((r) => (
              <div key={r.label}>
                <div className="flex justify-between text-xs"><span>{r.label}</span><span className="font-mono">{(r.v * 100).toFixed(0)}%</span></div>
                <Progress value={r.v * 100} className="h-2" />
              </div>
            ))}
          </div>
          <div className="mt-4 rounded-lg bg-amber-500/10 p-3 text-xs text-amber-700">
            <AlertTriangle className="mr-1 inline h-3 w-3" />Wakiso concentration above target 35% — diversify into Mukono &amp; Jinja next quarter.
          </div>
        </Card>
      </div>
    </>
  );
}

/* ========================= 2. PROFIT &amp; LOSS ========================= */
function ProfitAndLoss({ totals, schools, merchants }: { totals: any; schools: number; merchants: number }) {
  const revenue = totals.platformEarnings;
  const cogs = Math.round(revenue * 0.28); // payment rails + SMS + hosting + AI
  const grossProfit = revenue - cogs;
  const opex = {
    payroll: Math.round(revenue * 0.22),
    sales: Math.round(revenue * 0.08),
    marketing: Math.round(revenue * 0.05),
    customerSuccess: Math.round(revenue * 0.04),
    cloud: Math.round(revenue * 0.03),
    ai: Math.round(revenue * 0.025),
    legal: Math.round(revenue * 0.015),
    office: Math.round(revenue * 0.02),
  };
  const totalOpex = Object.values(opex).reduce((a, b) => a + b, 0);
  const ebitda = grossProfit - totalOpex;
  const tax = Math.round(Math.max(0, ebitda) * 0.30);
  const netIncome = ebitda - tax;

  const trend12 = Array.from({ length: 12 }, (_, i) => {
    const m = new Date(); m.setMonth(m.getMonth() - (11 - i));
    const rv = Math.round(revenue * (0.78 + i * 0.022));
    const cg = Math.round(rv * (0.32 - i * 0.004));
    const ox = Math.round(rv * (0.42 - i * 0.006));
    return { m: m.toLocaleString("en", { month: "short" }), revenue: rv, cogs: cg, opex: ox, ebitda: rv - cg - ox };
  });

  return (
    <>
      <div className="grid gap-3 md:grid-cols-5">
        <Kpi i={Banknote} label="Revenue" value={fmtCompact(revenue)} trend="+12%" up />
        <Kpi i={Coins} label="Gross profit" value={fmtCompact(grossProfit)} trend={`${((grossProfit / revenue) * 100).toFixed(0)}% margin`} up />
        <Kpi i={Briefcase} label="OPEX" value={fmtCompact(totalOpex)} trend={`${((totalOpex / revenue) * 100).toFixed(0)}% of rev`} />
        <Kpi i={TrendingUp} label="EBITDA" value={fmtCompact(ebitda)} trend={`${((ebitda / revenue) * 100).toFixed(0)}% margin`} up />
        <Kpi i={Scale} label="Net income" value={fmtCompact(netIncome)} trend="after tax 30%" up={netIncome >= 0} />
      </div>

      <Card className="p-4 shadow-card">
        <div className="font-display text-lg">12-month P&amp;L trend</div>
        <div className="h-72">
          <ResponsiveContainer><ComposedChart data={trend12}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
            <XAxis dataKey="m" tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${(v / 1e6).toFixed(0)}M`} />
            <Tooltip formatter={(v: any) => fmtCompact(v as number)} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="revenue" fill="#7B1416" />
            <Bar dataKey="cogs" fill="#A42022" />
            <Bar dataKey="opex" fill="#C4A882" />
            <Line dataKey="ebitda" stroke="#0ea5e9" strokeWidth={2} />
          </ComposedChart></ResponsiveContainer>
        </div>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="p-4 shadow-card">
          <div className="font-display text-lg">Income statement · this month</div>
          <div className="mt-3 divide-y text-sm">
            <PnLLine label="Subscription revenue" v={totals.subRevenue} />
            <PnLLine label="Merchant commissions" v={totals.merchantComm} />
            <PnLLine label="Wallet commissions" v={totals.walletComm} />
            <PnLLine label="Transport revenue" v={totals.transportRev} />
            <PnLLine label="Marketplace + SMS + AI" v={totals.marketplaceRev + totals.smsRev + totals.aiRev} />
            <PnLLine label="Total revenue" v={revenue} bold />
            <PnLLine label="COGS · payment + SMS + cloud + AI" v={-cogs} />
            <PnLLine label="Gross profit" v={grossProfit} bold tone="success" />
            <PnLLine label="Payroll" v={-opex.payroll} />
            <PnLLine label="Sales" v={-opex.sales} />
            <PnLLine label="Marketing" v={-opex.marketing} />
            <PnLLine label="Customer success" v={-opex.customerSuccess} />
            <PnLLine label="Cloud + infra" v={-opex.cloud} />
            <PnLLine label="AI providers" v={-opex.ai} />
            <PnLLine label="Legal + compliance" v={-opex.legal} />
            <PnLLine label="Office + admin" v={-opex.office} />
            <PnLLine label="EBITDA" v={ebitda} bold tone="success" />
            <PnLLine label="Tax provision (30%)" v={-tax} />
            <PnLLine label="Net income" v={netIncome} bold tone="primary" />
          </div>
        </Card>

        <Card className="p-4 shadow-card">
          <div className="font-display text-lg">OPEX breakdown</div>
          <div className="h-56">
            <ResponsiveContainer><PieChart>
              <Pie data={Object.entries(opex).map(([k, v]) => ({ name: k, value: v }))} dataKey="value" nameKey="name" innerRadius={40} outerRadius={80}>
                {Object.keys(opex).map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
              </Pie>
              <Tooltip formatter={(v: any) => fmtCompact(v as number)} />
            </PieChart></ResponsiveContainer>
          </div>
          <div className="grid grid-cols-2 gap-1.5 text-xs">
            {Object.entries(opex).map(([k, v], i) => (
              <div key={k} className="flex items-center justify-between">
                <div className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: PALETTE[i % PALETTE.length] }} /><span className="capitalize">{k}</span></div>
                <span className="font-mono">{fmtCompact(v)}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 rounded-lg bg-success/10 p-3 text-xs text-success">
            <CheckCircle2 className="mr-1 inline h-3 w-3" />EBITDA margin {((ebitda / revenue) * 100).toFixed(0)}% · above African SaaS median of 18%
          </div>
        </Card>
      </div>
    </>
  );
}
function PnLLine({ label, v, bold, tone }: { label: string; v: number; bold?: boolean; tone?: "success" | "primary" }) {
  const cls = tone === "success" ? "text-success" : tone === "primary" ? "text-primary" : v < 0 ? "text-destructive" : "";
  return (
    <div className={`flex items-center justify-between py-1.5 ${bold ? "font-display text-base" : ""}`}>
      <span>{label}</span>
      <span className={`font-mono ${cls}`}>{fmtCompact(v)}</span>
    </div>
  );
}

/* ========================= 3. ACCOUNTS RECEIVABLE ========================= */
function AccountsReceivable({ schoolFin }: { schoolFin: any[] }) {
  const totalOutstanding = schoolFin.reduce((a, s) => a + s.outstanding, 0);
  const aging = [
    { label: "Current", v: totalOutstanding * 0.46, color: "hsl(var(--success))", days: "0–30" },
    { label: "31–60", v: totalOutstanding * 0.28, color: "#C4A882", days: "31–60" },
    { label: "61–90", v: totalOutstanding * 0.16, color: "#A42022", days: "61–90" },
    { label: "90+", v: totalOutstanding * 0.10, color: "hsl(var(--destructive))", days: "90+" },
  ];
  const dso = 38;
  const collectRate = schoolFin.reduce((a, s) => a + s.rate, 0) / Math.max(1, schoolFin.length);

  return (
    <>
      <div className="grid gap-3 md:grid-cols-4">
        <Kpi i={HandCoins} label="Total receivable" value={fmtCompact(totalOutstanding)} />
        <Kpi i={CalendarClock} label="DSO" value={`${dso} days`} trend="-3 days" up />
        <Kpi i={Gauge} label="Collection rate" value={`${(collectRate * 100).toFixed(0)}%`} trend="+3 pts" up />
        <Kpi i={AlertTriangle} label="Over 90 days" value={fmtCompact(totalOutstanding * 0.10)} trend="write-off risk" />
      </div>

      <div className="grid gap-3 md:grid-cols-4">
        {aging.map((a) => (
          <Card key={a.label} className="p-3 shadow-card">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{a.label} · {a.days}d</div>
              <span className="h-2 w-2 rounded-full" style={{ background: a.color }} />
            </div>
            <div className="mt-1 font-display text-xl">{fmtCompact(a.v)}</div>
            <Progress value={(a.v / totalOutstanding) * 100} className="mt-2 h-1.5" />
          </Card>
        ))}
      </div>

      <Card className="overflow-hidden shadow-card">
        <div className="border-b p-3 font-display text-lg flex items-center justify-between">
          <span>Top outstanding · schools</span>
          <div className="flex gap-1">
            <Button size="sm" variant="outline"><FileText className="mr-1 h-3 w-3" />Send reminders</Button>
            <Button size="sm" variant="outline">Bulk dunning</Button>
          </div>
        </div>
        <div className="divide-y max-h-[420px] overflow-auto">
          {schoolFin.slice(0, 16).map((s) => {
            const days = 15 + (Math.abs(hash(s.id)) % 90);
            const bucket = days > 90 ? "destructive" : days > 60 ? "warn" : days > 30 ? "amber" : "ok";
            return (
              <div key={s.id} className="grid grid-cols-[2fr_70px_1fr_1fr_1fr_auto] items-center gap-3 p-3 text-sm">
                <div><div className="font-medium">{s.name}</div><div className="text-[11px] text-muted-foreground">{s.district} · {s.subscription_plan}</div></div>
                <Badge variant={bucket === "destructive" ? "destructive" : "secondary"} className="text-[10px]">{days}d</Badge>
                <div className="text-xs">Expected<div className="font-mono">{fmtCompact(s.expected)}</div></div>
                <div className="text-xs">Outstanding<div className="font-mono text-destructive">{fmtCompact(s.outstanding)}</div></div>
                <div className="flex items-center gap-2"><Progress value={s.rate * 100} className="h-2 flex-1" /><span className="w-9 text-right text-xs">{(s.rate * 100).toFixed(0)}%</span></div>
                <div className="flex gap-1"><Button size="sm" variant="outline">Remind</Button></div>
              </div>
            );
          })}
        </div>
      </Card>
    </>
  );
}

/* ========================= 4. ACCOUNTS PAYABLE ========================= */
function AccountsPayable({ settlements, merchants }: { settlements: any[]; merchants: any[] }) {
  const queue = [
    { stage: "Queued", n: settlements.filter((s: any) => s.status === "queued").length || 14, color: "#C4A882", v: 28_400_000 },
    { stage: "Approved", n: settlements.filter((s: any) => s.status === "approved").length || 6, color: "#0ea5e9", v: 12_900_000 },
    { stage: "Processing", n: settlements.filter((s: any) => s.status === "processing").length || 8, color: "#9333ea", v: 9_800_000 },
    { stage: "Paid", n: settlements.filter((s: any) => s.status === "paid").length || 142, color: "#7B1416", v: 318_000_000 },
    { stage: "Held", n: 1, color: "#A42022", v: 1_200_000 },
    { stage: "Failed", n: 2, color: "#B33A1A", v: 480_000 },
  ];
  const dpo = 4.2;
  const pendingPayouts = queue.filter(q => ["Queued", "Approved", "Processing"].includes(q.stage)).reduce((a, q) => a + q.v, 0);

  const topPayees = merchants.slice(0, 8).map((m: any, i: number) => ({
    name: m.name ?? `Merchant ${i + 1}`,
    category: m.category ?? "general",
    due: 800_000 + (Math.abs(hash(m.id ?? String(i))) % 4_000_000),
    days: 1 + (Math.abs(hash(m.id ?? String(i))) % 7),
  })).sort((a, b) => b.due - a.due);

  return (
    <>
      <div className="grid gap-3 md:grid-cols-4">
        <Kpi i={Landmark} label="Pending payouts" value={fmtCompact(pendingPayouts)} trend={`${dpo} day DPO`} />
        <Kpi i={CheckCircle2} label="Paid · MTD" value={fmtCompact(318_000_000)} trend="142 settlements" up />
        <Kpi i={AlertTriangle} label="Held / disputed" value={fmtCompact(1_680_000)} trend="3 cases" />
        <Kpi i={Briefcase} label="Vendor accounts" value={fmtNum(merchants.length)} trend={`${merchants.filter((m: any) => m.active).length} active`} />
      </div>

      <Card className="overflow-hidden shadow-card">
        <div className="border-b p-3 flex items-center justify-between">
          <div className="font-display text-lg">Settlement queue</div>
          <div className="flex gap-1">
            <Button size="sm" variant="outline">Approve batch</Button>
            <Button size="sm" variant="outline">Release</Button>
            <Button size="sm" variant="outline">Investigate</Button>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 p-3 md:grid-cols-6">
          {queue.map((q) => (
            <div key={q.stage} className="rounded-lg border bg-card p-3 text-center">
              <div className="h-1 w-full rounded" style={{ background: q.color }} />
              <div className="mt-2 font-display text-2xl">{q.n}</div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{q.stage}</div>
              <div className="mt-1 font-mono text-[10px] text-muted-foreground">{fmtCompact(q.v)}</div>
            </div>
          ))}
        </div>
      </Card>

      <Card className="overflow-hidden shadow-card">
        <div className="border-b p-3 font-display text-lg">Top payees · due now</div>
        <div className="divide-y">
          {topPayees.map((p, i) => (
            <div key={i} className="grid grid-cols-[2fr_1fr_1fr_1fr_auto] items-center gap-3 p-3 text-sm">
              <div><div className="font-medium">{p.name}</div><div className="text-[11px] text-muted-foreground capitalize">{p.category}</div></div>
              <Badge variant="secondary" className="justify-self-start text-[10px]">in {p.days}d</Badge>
              <div className="font-mono">{fmtCompact(p.due)}</div>
              <Progress value={(7 - p.days) / 7 * 100} className="h-2" />
              <div className="flex gap-1"><Button size="sm" variant="outline">Pay</Button><Button size="sm" variant="outline">Hold</Button></div>
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-4 shadow-card">
        <div className="font-display text-lg">Cash outflow forecast · next 14 days</div>
        <div className="h-56">
          <ResponsiveContainer><AreaChart data={Array.from({ length: 14 }, (_, i) => ({ d: `D${i + 1}`, payout: Math.round(2_000_000 + Math.random() * 4_000_000) }))}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
            <XAxis dataKey="d" tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${(v / 1e6).toFixed(0)}M`} />
            <Tooltip formatter={(v: any) => fmtCompact(v as number)} />
            <Area dataKey="payout" stroke="#A42022" fill="#A42022" fillOpacity={0.3} />
          </AreaChart></ResponsiveContainer>
        </div>
      </Card>
    </>
  );
}

/* ========================= 5. SCHOOL FINANCIALS ========================= */
function SchoolFinancials({ schoolFin }: { schoolFin: any[] }) {
  const [search, setSearch] = useState("");
  const filtered = schoolFin.filter(s => !search || s.name.toLowerCase().includes(search.toLowerCase()) || (s.district ?? "").toLowerCase().includes(search.toLowerCase()));

  const summary = useMemo(() => {
    const totalExpected = filtered.reduce((a, s) => a + s.expected, 0);
    const totalCollected = filtered.reduce((a, s) => a + s.collected, 0);
    const totalOutstanding = filtered.reduce((a, s) => a + s.outstanding, 0);
    const avgHealth = filtered.reduce((a, s) => a + s.finHealth, 0) / Math.max(1, filtered.length);
    return { totalExpected, totalCollected, totalOutstanding, avgHealth };
  }, [filtered]);

  return (
    <>
      <div className="grid gap-3 md:grid-cols-4">
        <Kpi i={Banknote} label="Expected · network" value={fmtCompact(summary.totalExpected)} />
        <Kpi i={CheckCircle2} label="Collected" value={fmtCompact(summary.totalCollected)} trend={`${((summary.totalCollected / Math.max(1, summary.totalExpected)) * 100).toFixed(0)}%`} up />
        <Kpi i={AlertTriangle} label="Outstanding" value={fmtCompact(summary.totalOutstanding)} />
        <Kpi i={Gauge} label="Avg financial health" value={`${Math.round(summary.avgHealth)}/100`} trend="+4" up />
      </div>

      <Card className="overflow-hidden shadow-card">
        <div className="border-b p-3 flex items-center justify-between gap-2">
          <div className="font-display text-lg flex items-center gap-2"><Gauge className="h-4 w-4 text-primary" />School Financial Health Index</div>
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…" className="w-60" />
        </div>
        <div className="divide-y max-h-[520px] overflow-auto">
          {filtered.slice(0, 30).map((s, i) => (
            <div key={s.id} className="grid grid-cols-[28px_2.2fr_60px_1fr_1fr_1fr_70px] items-center gap-3 p-3 text-sm">
              <div className="font-mono text-muted-foreground">#{i + 1}</div>
              <div><div className="font-medium">{s.name}</div><div className="text-[11px] text-muted-foreground">{s.district} · {s.subscription_plan}</div></div>
              <div className={`grid h-11 w-11 place-items-center rounded-full border-2 font-display text-sm ${s.finHealth >= 80 ? "border-success text-success" : s.finHealth >= 65 ? "border-amber-500 text-amber-600" : "border-destructive text-destructive"}`}>
                {s.finHealth}
              </div>
              <div className="text-xs">Expected<div className="font-mono">{fmtCompact(s.expected)}</div></div>
              <div className="text-xs">Collected<div className="font-mono text-success">{fmtCompact(s.collected)}</div></div>
              <div className="text-xs">Outstanding<div className="font-mono text-destructive">{fmtCompact(s.outstanding)}</div></div>
              <div className="flex items-center gap-2"><Progress value={s.rate * 100} className="h-2 flex-1" /><span className="w-9 text-right text-xs">{(s.rate * 100).toFixed(0)}%</span></div>
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-4 shadow-card">
        <div className="font-display text-lg">Collection rate vs school size</div>
        <div className="h-64">
          <ResponsiveContainer><ScatterChart>
            <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
            <XAxis type="number" dataKey="student_count" name="Students" tick={{ fontSize: 10 }} />
            <YAxis type="number" dataKey="rate" name="Rate" tick={{ fontSize: 10 }} tickFormatter={(v) => `${(v * 100).toFixed(0)}%`} domain={[0.4, 1]} />
            <Tooltip cursor={{ strokeDasharray: "3 3" }} formatter={(v: any, n: any) => n === "rate" ? `${(v * 100).toFixed(0)}%` : v} />
            <Scatter data={filtered.slice(0, 80)} fill="#7B1416" />
          </ScatterChart></ResponsiveContainer>
        </div>
      </Card>
    </>
  );
}

/* ========================= 6. WALLET FINANCE ========================= */
function WalletFinance({ totals }: { totals: any }) {
  const float = { parent: 230_000_000, student: 420_000_000, merchant: 80_000_000, escrow: 35_000_000, transport: 22_000_000 };
  const floatTotal = Object.values(float).reduce((a, b) => a + b, 0);
  const wallets = { parent: 22_800, student: 48_200, merchant: 412, transport: 86 };
  const topupRails = [
    { name: "MTN MoMo", v: 240_000_000, color: "#fbbf24" },
    { name: "Airtel Money", v: 180_000_000, color: "#B33A1A" },
    { name: "Bank cards", v: 60_000_000, color: "#7B1416" },
  ];
  const flow = Array.from({ length: 14 }, (_, i) => ({
    d: `D${i + 1}`,
    inflow: Math.round(12_000_000 + Math.random() * 8_000_000),
    outflow: Math.round(10_000_000 + Math.random() * 8_000_000),
  }));

  return (
    <>
      <div className="grid gap-3 md:grid-cols-5">
        <Kpi i={Wallet} label="Total float · custody" value={fmtCompact(floatTotal)} trend="held in trust" />
        <Kpi i={Users2} label="Parent wallets" value={fmtNum(wallets.parent)} trend="+8% MoM" up />
        <Kpi i={Users2} label="Student wallets" value={fmtNum(wallets.student)} trend="+14% MoM" up />
        <Kpi i={Store} label="Merchant wallets" value={fmtNum(wallets.merchant)} />
        <Kpi i={Coins} label="Wallet revenue" value={fmtCompact(totals.walletComm)} trend="+22%" up />
      </div>

      <Card className="p-4 shadow-card">
        <div className="font-display text-lg">Float composition · custody</div>
        <p className="text-[11px] text-muted-foreground">Money TriSphere holds in trust. Reconciled hourly · ringfenced in segregated trust account.</p>
        <div className="mt-3 h-3 w-full overflow-hidden rounded-full bg-muted">
          <div className="flex h-full">
            {Object.entries(float).map(([k, v], i) => (
              <div key={k} title={`${k} ${fmtCompact(v)}`} style={{ width: `${(v / floatTotal) * 100}%`, background: PALETTE[i] }} />
            ))}
          </div>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2 md:grid-cols-5">
          {Object.entries(float).map(([k, v], i) => (
            <div key={k} className="rounded-lg border bg-card p-3">
              <div className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ background: PALETTE[i] }} /><span className="text-[10px] uppercase tracking-wider text-muted-foreground">{k}</span></div>
              <div className="mt-1 font-display text-base">{fmtCompact(v)}</div>
            </div>
          ))}
        </div>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="p-4 shadow-card">
          <div className="font-display text-lg">Top-up rails · MTD</div>
          <div className="h-52">
            <ResponsiveContainer><PieChart>
              <Pie data={topupRails} dataKey="v" nameKey="name" innerRadius={45} outerRadius={75}>
                {topupRails.map((r, i) => <Cell key={i} fill={r.color} />)}
              </Pie>
              <Tooltip formatter={(v: any) => fmtCompact(v as number)} />
            </PieChart></ResponsiveContainer>
          </div>
          <div className="space-y-1 text-xs">
            {topupRails.map((r) => (
              <div key={r.name} className="flex items-center justify-between">
                <div className="flex items-center gap-2"><span className="h-2 w-2 rounded-full" style={{ background: r.color }} />{r.name}</div>
                <div className="font-mono">{fmtCompact(r.v)}</div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-4 shadow-card">
          <div className="font-display text-lg">14-day wallet flow</div>
          <div className="h-52">
            <ResponsiveContainer><AreaChart data={flow}>
              <defs>
                <linearGradient id="in" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#7B1416" stopOpacity={0.4} /><stop offset="100%" stopColor="#7B1416" stopOpacity={0} /></linearGradient>
                <linearGradient id="out" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#A42022" stopOpacity={0.4} /><stop offset="100%" stopColor="#A42022" stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
              <XAxis dataKey="d" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${(v / 1e6).toFixed(0)}M`} />
              <Tooltip formatter={(v: any) => fmtCompact(v as number)} />
              <Area dataKey="inflow" stroke="#7B1416" fill="url(#in)" />
              <Area dataKey="outflow" stroke="#A42022" fill="url(#out)" />
            </AreaChart></ResponsiveContainer>
          </div>
        </Card>
      </div>

      <Card className="p-4 shadow-card">
        <div className="font-display text-lg">Reconciliation status</div>
        <div className="mt-3 grid gap-2 md:grid-cols-4">
          <ReconBox label="Auto-reconciled" v={98.6} ok />
          <ReconBox label="Pending match" v={1.1} />
          <ReconBox label="Manual review" v={0.3} />
          <ReconBox label="Variance > 0.1%" v={0} ok />
        </div>
      </Card>
    </>
  );
}
function ReconBox({ label, v, ok }: { label: string; v: number; ok?: boolean }) {
  return (
    <div className="rounded-lg border bg-card p-3">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`mt-1 font-display text-2xl ${ok ? "text-success" : ""}`}>{v.toFixed(1)}%</div>
      <Progress value={v} className="mt-1 h-1.5" />
    </div>
  );
}

/* ========================= 7. FORECASTING ========================= */
function ForecastingCenter({ baseRevenue, baseSchools, districts }: { baseRevenue: number; baseSchools: number; districts: any[] }) {
  const [newSchools, setNewSchools] = useState(0);
  const [feeChange, setFeeChange] = useState(0);
  const [walletAdoption, setWalletAdoption] = useState(0);
  const [merchantGrowth, setMerchantGrowth] = useState(0);
  const [churn, setChurn] = useState(2);

  const projection = useMemo(() => {
    const subUplift = newSchools * 1_800_000;
    const feeUplift = baseRevenue * (feeChange / 100);
    const walletUplift = baseRevenue * 0.15 * (walletAdoption / 100);
    const merchantUplift = baseRevenue * 0.12 * (merchantGrowth / 100);
    const churnDrag = baseRevenue * (churn / 100);
    const total = baseRevenue + subUplift + feeUplift + walletUplift + merchantUplift - churnDrag;
    const delta = total - baseRevenue;
    const pct = (delta / baseRevenue) * 100;
    return { total, delta, pct, subUplift, feeUplift, walletUplift, merchantUplift, churnDrag };
  }, [newSchools, feeChange, walletAdoption, merchantGrowth, churn, baseRevenue]);

  const months = Array.from({ length: 18 }, (_, i) => {
    const m = new Date(); m.setMonth(m.getMonth() + i);
    const conservative = projection.total * Math.pow(1.06, i);
    const expected = projection.total * Math.pow(1.10, i);
    const aggressive = projection.total * Math.pow(1.16, i);
    return { m: m.toLocaleString("en", { month: "short", year: "2-digit" }), conservative, expected, aggressive };
  });

  return (
    <>
      <Card className="overflow-hidden shadow-card">
        <div className="border-b bg-gradient-to-r from-primary/10 to-transparent p-3">
          <div className="flex items-center gap-2 font-display text-lg"><FlaskConical className="h-4 w-4 text-primary" />Financial digital twin · scenario engine</div>
          <p className="text-xs text-muted-foreground">Simulate the entire ecosystem and project 18 months out.</p>
        </div>
        <div className="grid gap-5 p-4 lg:grid-cols-2">
          <div className="space-y-4">
            <SimSlider label="Onboard new schools" value={newSchools} onChange={setNewSchools} min={0} max={200} unit="schools" />
            <SimSlider label="Transaction fee adjustment" value={feeChange} onChange={setFeeChange} min={-2} max={5} step={0.1} unit="%" />
            <SimSlider label="Parent wallet adoption uplift" value={walletAdoption} onChange={setWalletAdoption} min={0} max={100} unit="%" />
            <SimSlider label="Merchant network growth" value={merchantGrowth} onChange={setMerchantGrowth} min={0} max={100} unit="%" />
            <SimSlider label="Monthly churn" value={churn} onChange={setChurn} min={0} max={10} step={0.1} unit="%" />
            <Button size="sm" variant="outline" onClick={() => { setNewSchools(0); setFeeChange(0); setWalletAdoption(0); setMerchantGrowth(0); setChurn(2); }} className="w-full">Reset</Button>
          </div>
          <div className="rounded-xl border bg-gradient-to-br from-primary/10 via-card to-card p-4">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Projected monthly revenue</div>
            <div className="mt-2 font-display text-4xl text-primary tabular-nums">{fmtCompact(projection.total)}</div>
            <div className={`mt-1 flex items-center gap-1 text-sm ${projection.delta >= 0 ? "text-success" : "text-destructive"}`}>
              {projection.delta >= 0 ? <ArrowUpRight className="h-4 w-4" /> : <ArrowDownRight className="h-4 w-4" />}
              {fmtCompact(Math.abs(projection.delta))} ({projection.pct >= 0 ? "+" : ""}{projection.pct.toFixed(1)}%)
            </div>
            <div className="mt-4 space-y-1.5 text-xs">
              <Row label="From new schools" value={fmtCompact(projection.subUplift)} />
              <Row label="From fee change" value={fmtCompact(projection.feeUplift)} />
              <Row label="From wallet adoption" value={fmtCompact(projection.walletUplift)} />
              <Row label="From merchant growth" value={fmtCompact(projection.merchantUplift)} />
              <Row label="Churn drag" value={`-${fmtCompact(projection.churnDrag)}`} />
            </div>
            <div className="mt-4 rounded-lg bg-card/60 p-3 text-[11px] text-muted-foreground">
              Annualized: <span className="font-mono text-foreground">{fmtCompact(projection.total * 12)}</span> · {baseSchools + newSchools} schools modelled
            </div>
          </div>
        </div>
      </Card>

      <Card className="p-4 shadow-card">
        <div className="font-display text-lg">18-month projection · 3 scenarios</div>
        <div className="h-72">
          <ResponsiveContainer><AreaChart data={months}>
            <defs>
              <linearGradient id="exp" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#7B1416" stopOpacity={0.4} /><stop offset="100%" stopColor="#7B1416" stopOpacity={0} /></linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
            <XAxis dataKey="m" tick={{ fontSize: 9 }} />
            <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${(v / 1e6).toFixed(0)}M`} />
            <Tooltip formatter={(v: any) => fmtCompact(v as number)} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Area dataKey="aggressive" stroke="#C4A882" fill="#C4A882" fillOpacity={0.15} name="Aggressive (+16%)" />
            <Area dataKey="expected" stroke="#7B1416" fill="url(#exp)" name="Expected (+10%)" />
            <Area dataKey="conservative" stroke="#A42022" fill="#A42022" fillOpacity={0.1} name="Conservative (+6%)" />
          </AreaChart></ResponsiveContainer>
        </div>
      </Card>

      <Card className="p-4 shadow-card">
        <div className="font-display text-lg">Forecast triggers · AI alerts</div>
        <div className="mt-2 divide-y text-sm">
          {[
            { tone: "warn", t: "If wallet adoption stays flat, you miss 2027 plan by UGX 1.4B.", a: "Run parent activation in Mukono &amp; Jinja." },
            { tone: "ok", t: "Greenhill + Royal cluster projected to add UGX 280M ARR in 6 months.", a: "Lock multi-year contracts now." },
            { tone: "warn", t: "Fee uplift above 0.5% historically triggers a 1.2 pt churn spike.", a: "Pair fee change with new value (transport)." },
            { tone: "ok", t: "Onboarding 50 schools in Wakiso has 92% conversion probability.", a: "Greenlight Wakiso campaign." },
          ].map((x, i) => (
            <div key={i} className="grid grid-cols-[24px_1fr_auto] items-center gap-3 py-3">
              <span className={`grid h-6 w-6 place-items-center rounded-full ${x.tone === "warn" ? "bg-amber-500/15 text-amber-600" : "bg-success/15 text-success"}`}>{x.tone === "warn" ? <AlertTriangle className="h-3.5 w-3.5" /> : <CheckCircle2 className="h-3.5 w-3.5" />}</span>
              <div><div>{x.t}</div><div className="text-[11px] text-muted-foreground">{x.a}</div></div>
              <Button size="sm" variant="outline">Act</Button>
            </div>
          ))}
        </div>
      </Card>
    </>
  );
}

function SimSlider({ label, value, onChange, min, max, step = 1, unit }: { label: string; value: number; onChange: (n: number) => void; min: number; max: number; step?: number; unit: string }) {
  return (
    <div>
      <div className="mb-1 flex items-center justify-between text-xs">
        <span className="font-medium">{label}</span>
        <span className="font-mono text-primary">{value > 0 ? "+" : ""}{value}{unit === "%" ? "%" : ` ${unit}`}</span>
      </div>
      <Slider value={[value]} onValueChange={(v) => onChange(v[0])} min={min} max={max} step={step} />
    </div>
  );
}

/* ========================= 8. DISTRICT ANALYTICS ========================= */
function DistrictAnalytics({ districts }: { districts: any[] }) {
  const ref = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const overlaysRef = useRef<any[]>([]);
  const [ready, setReady] = useState(false);
  const [metric, setMetric] = useState<"revenue" | "collected" | "outstanding" | "adoption">("revenue");
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    loadGoogleMaps().then(() => {
      if (!ref.current) return;
      mapRef.current = new window.google.maps.Map(ref.current, {
        center: { lat: 1.3733, lng: 32.2903 }, zoom: 7, disableDefaultUI: true, zoomControl: true,
        styles: [{ featureType: "poi", stylers: [{ visibility: "off" }] }],
      });
      setReady(true);
    }).catch((e) => setErr(e.message));
  }, []);

  useEffect(() => {
    if (!ready || !mapRef.current) return;
    overlaysRef.current.forEach((c) => c.setMap(null));
    overlaysRef.current = [];
    const max = Math.max(1, ...districts.map((d) => d[metric] as number));
    districts.filter((d) => d.lat && d.lng).forEach((d) => {
      const intensity = (d[metric] as number) / max;
      const radius = 8_000 + intensity * 22_000;
      const colors = metric === "outstanding" ? ["#fef3c7", "#B33A1A"] : metric === "adoption" ? ["#fef3c7", "#0ea5e9"] : ["#dcfce7", "#7B1416"];
      const circle = new window.google.maps.Circle({
        map: mapRef.current, center: { lat: d.lat, lng: d.lng }, radius,
        fillColor: intensity > 0.6 ? colors[1] : colors[0], fillOpacity: 0.35 + intensity * 0.35,
        strokeColor: colors[1], strokeOpacity: 0.7, strokeWeight: 1.5,
      });
      overlaysRef.current.push(circle);
    });
  }, [ready, districts, metric]);

  const opportunity = districts.slice().sort((a, b) => (b.revenue / Math.max(1, b.schools)) - (a.revenue / Math.max(1, a.schools))).slice(0, 6);

  return (
    <>
      <Card className="overflow-hidden shadow-card">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b p-3">
          <div className="flex items-center gap-2 font-display text-lg"><MapPin className="h-4 w-4" />Uganda economic heatmap</div>
          <div className="flex flex-wrap gap-1">
            {(["revenue", "collected", "outstanding", "adoption"] as const).map((m) => (
              <Button key={m} size="sm" variant={metric === m ? "default" : "outline"} onClick={() => setMetric(m)} className="h-7 px-2 text-xs capitalize">{m}</Button>
            ))}
          </div>
        </div>
        <div className="relative">
          <div ref={ref} style={{ height: 460 }} className="w-full bg-muted" />
          {err && <div className="absolute inset-0 grid place-items-center text-sm">{err}</div>}
          {!ready && !err && <div className="absolute inset-0 grid place-items-center text-xs text-muted-foreground">Loading map…</div>}
        </div>
      </Card>

      <Card className="overflow-hidden shadow-card">
        <div className="border-b p-3 font-display text-lg">District financial breakdown</div>
        <div className="divide-y max-h-[420px] overflow-auto">
          {districts.map((d) => (
            <div key={d.name} className="grid grid-cols-[1.4fr_70px_1fr_1fr_1fr_90px] items-center gap-3 p-3 text-sm">
              <div className="flex items-center gap-2 font-medium"><MapPin className="h-3 w-3 text-muted-foreground" />{d.name}</div>
              <div className="text-[11px] text-muted-foreground">{d.schools} schools</div>
              <div className="font-mono text-xs">{fmtCompact(d.revenue)}</div>
              <div className="font-mono text-xs text-success">{fmtCompact(d.collected)}</div>
              <div className="font-mono text-xs text-destructive">{fmtCompact(d.outstanding)}</div>
              <Progress value={(d.collected / Math.max(1, d.revenue)) * 100} className="h-2" />
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-4 shadow-card">
        <div className="font-display text-lg">Opportunity score · ARPU per school by district</div>
        <div className="h-64">
          <ResponsiveContainer><BarChart data={opportunity.map(d => ({ name: d.name, arpu: d.revenue / Math.max(1, d.schools) }))}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
            <XAxis dataKey="name" tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${(v / 1e6).toFixed(1)}M`} />
            <Tooltip formatter={(v: any) => fmtCompact(v as number)} />
            <Bar dataKey="arpu" fill="#7B1416" />
          </BarChart></ResponsiveContainer>
        </div>
      </Card>
    </>
  );
}

/* ========================= 9. AUDIT & TAX ========================= */
function AuditTax({ totals }: { totals: any }) {
  return (
    <>
      <div className="grid gap-3 md:grid-cols-4">
        <Kpi i={Shield} label="Audit events · 30d" value={fmtNum(12_840)} trend="append-only" up />
        <Kpi i={ListChecks} label="Manual adjustments" value="42" trend="needs review" />
        <Kpi i={Receipt} label="URA VAT prepared" value={fmtCompact(18_400_000)} trend="filed monthly" up />
        <Kpi i={Banknote} label="Refund overrides" value="11" trend="this term" />
      </div>

      <Card className="overflow-hidden shadow-card">
        <div className="flex items-center justify-between border-b p-3">
          <div className="font-display text-lg">Uganda tax centre</div>
          <Button size="sm" variant="outline"><Download className="mr-1 h-3 w-3" />Export URA pack</Button>
        </div>
        <div className="grid gap-3 p-4 md:grid-cols-4">
          <TaxCard title="VAT · 18%" amt={18_400_000} period="Last month" />
          <TaxCard title="Withholding tax" amt={3_200_000} period="Last month" />
          <TaxCard title="Stamp duty" amt={1_240_000} period="MTD" />
          <TaxCard title="Platform revenue" amt={totals.platformEarnings} period="MTD" />
        </div>
      </Card>

      <Card className="overflow-hidden shadow-card">
        <div className="border-b p-3 font-display text-lg flex items-center gap-2"><FileCheck2 className="h-4 w-4" />Compliance posture</div>
        <div className="grid gap-3 p-4 md:grid-cols-4">
          {[
            { k: "PCI-DSS scope", v: "Compliant", ok: true },
            { k: "BoU sandbox", v: "Approved", ok: true },
            { k: "UCC data localisation", v: "Compliant", ok: true },
            { k: "ISO 27001", v: "In progress", ok: false },
          ].map((c) => (
            <div key={c.k} className="rounded-lg border bg-card p-3">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{c.k}</div>
              <div className={`mt-1 font-display text-base ${c.ok ? "text-success" : "text-amber-600"}`}>{c.v}</div>
            </div>
          ))}
        </div>
      </Card>

      <Card className="overflow-hidden shadow-card">
        <div className="border-b p-3 font-display text-lg flex items-center gap-2"><ScrollText className="h-4 w-4" />Recent audit events</div>
        <div className="divide-y">
          {[
            { who: "superadmin@schoolos", act: "Released settlement batch STL-20260605-014", when: "12 min ago" },
            { who: "finance@schoolos", act: "Manual adjustment · -UGX 240,000 (refund override)", when: "1 hour ago" },
            { who: "system", act: "Auto-hold triggered · Equator Canteen (refund rate)", when: "3 hours ago" },
            { who: "superadmin@schoolos", act: "Approved settlement release · 14 merchants", when: "5 hours ago" },
            { who: "system", act: "URA VAT export prepared · period 2026-05", when: "Yesterday" },
          ].map((e, i) => (
            <div key={i} className="grid grid-cols-[180px_1fr_120px] items-center gap-3 p-3 text-sm">
              <div className="font-mono text-xs text-muted-foreground">{e.who}</div>
              <div>{e.act}</div>
              <div className="text-xs text-muted-foreground">{e.when}</div>
            </div>
          ))}
        </div>
      </Card>
    </>
  );
}

/* ========================= 10. AI COPILOT & LEAK DETECTOR ========================= */
function RevenueLeakDetector({ totals, settlements, merchants }: any) {
  const leaks = [
    { kind: "Unreconciled settlement", merchant: "Royal Outfitters", amount: 2_400_000, since: "5 days", severity: "high", reason: "Batch STL-20260601 missing acknowledgement" },
    { kind: "Dormant merchant balance", merchant: "Capital Bookshop", amount: 1_180_000, since: "47 days", severity: "medium", reason: "No payout request; account inactive" },
    { kind: "Subscription underbilling", merchant: "Greenhill Academy", amount: 850_000, since: "current term", severity: "medium", reason: "Enterprise modules used on standard plan" },
    { kind: "Refund spike", merchant: "Equator Canteen", amount: 620_000, since: "this week", severity: "high", reason: "Refund rate jumped to 6.2% — investigate" },
    { kind: "Failed wallet topup", merchant: "MTN gateway", amount: 4_200_000, since: "2 days", severity: "medium", reason: "Stuck pending — 38 transactions" },
    { kind: "Stale invoice", merchant: "Kampala Parents Schl", amount: 1_900_000, since: "60 days", severity: "low", reason: "Term 1 invoice not closed" },
  ];
  const totalLeak = leaks.reduce((a, b) => a + b.amount, 0);
  return (
    <Card className="overflow-hidden shadow-card">
      <div className="border-b bg-gradient-to-r from-destructive/10 to-transparent p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 font-display text-lg"><Radar className="h-4 w-4 text-destructive" />Revenue leak detector</div>
          <Badge variant="destructive">{fmtCompact(totalLeak)} recoverable</Badge>
        </div>
        <p className="text-xs text-muted-foreground">AI scans settlements, refunds, dormant balances and underbilling across the network.</p>
      </div>
      <div className="divide-y">
        {leaks.map((l, i) => (
          <div key={i} className="grid grid-cols-[auto_2fr_1fr_1fr_auto] items-center gap-3 p-3 text-sm">
            <div className={`grid h-8 w-8 place-items-center rounded-full ${l.severity === "high" ? "bg-destructive/15 text-destructive" : l.severity === "medium" ? "bg-amber-500/15 text-amber-600" : "bg-muted text-muted-foreground"}`}><AlertTriangle className="h-4 w-4" /></div>
            <div><div className="font-medium">{l.kind}</div><div className="text-[11px] text-muted-foreground">{l.merchant} · {l.reason}</div></div>
            <div className="text-xs text-muted-foreground">{l.since}</div>
            <div className="text-right font-mono">{fmtCompact(l.amount)}</div>
            <Button size="sm" variant="outline">Recover</Button>
          </div>
        ))}
      </div>
    </Card>
  );
}

function CFOCopilot({ totals, districts }: { totals: any; districts: any[] }) {
  const [q, setQ] = useState("");
  const top = districts[0];
  const insights = [
    `${top?.name ?? "Wakiso"} contributes ${((top?.revenue ?? 0) / Math.max(1, districts.reduce((a, d) => a + d.revenue, 0)) * 100).toFixed(0)}% of revenue — prioritise neighbouring Mukono for expansion.`,
    `Boarding schools generate 2.4× the transaction volume of day schools — push transport + canteen modules to boarding-only segment.`,
    `Wallet revenue grew 22% MoM — pursue parent activation in lower-adoption districts: ${districts.slice(-3).map(d => d.name).join(", ")}.`,
    `Forecast: ${fmtCompact(totals.platformEarnings * 14.2)} ARR within 12 months if current MoM growth holds (+12%).`,
    `Refund rate sits at 0.6% network-wide — flag 2 merchants exceeding 4% (visible in Operations).`,
    `If transaction fee rises 0.3% and wallet adoption climbs 25%, projected uplift is ${fmtCompact(totals.gross * 0.003 + totals.gross * 0.15 * 0.25)} per month.`,
  ];
  return (
    <Card className="overflow-hidden shadow-card">
      <div className="border-b bg-gradient-to-r from-primary/10 to-transparent p-3">
        <div className="flex items-center gap-2 font-display text-lg"><Brain className="h-4 w-4 text-primary" />AI CFO Copilot</div>
        <p className="text-xs text-muted-foreground">Ask in plain English — answers computed across the entire network.</p>
      </div>
      <div className="flex gap-2 p-3">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Which district will generate the most revenue next year?" />
        <Button><Search className="mr-1 h-3 w-3" />Ask</Button>
      </div>
      <div className="divide-y">
        {insights.map((s, i) => (
          <div key={i} className="flex items-start gap-3 p-4">
            <div className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-primary/10 text-primary"><Sparkles className="h-3.5 w-3.5" /></div>
            <div className="text-sm">{s}</div>
            <ChevronRight className="ml-auto h-4 w-4 text-muted-foreground" />
          </div>
        ))}
      </div>
    </Card>
  );
}

/* ========================= shared ========================= */
function Kpi({ i: I, label, value, trend, up }: { i: any; label: string; value: string; trend?: string; up?: boolean }) {
  return (
    <Card className="p-4 shadow-card">
      <div className="flex items-start justify-between">
        <I className="h-4 w-4 text-muted-foreground" />
        {trend && <Badge variant="secondary" className="text-[10px]">{up ? <ArrowUpRight className="mr-0.5 h-3 w-3 text-success" /> : <ArrowDownRight className="mr-0.5 h-3 w-3 text-destructive" />}{trend}</Badge>}
      </div>
      <div className="mt-2 font-display text-2xl">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </Card>
  );
}
function TaxCard({ title, amt, period }: { title: string; amt: number; period: string }) {
  return (
    <div className="rounded-xl border bg-card p-4">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{title}</div>
      <div className="mt-1 font-display text-xl">{fmtCompact(amt)}</div>
      <div className="text-[11px] text-muted-foreground">{period}</div>
    </div>
  );
}
function Row({ label, value }: { label: string; value: string }) {
  return <div className="flex items-center justify-between text-xs"><span className="text-muted-foreground">{label}</span><span className="font-mono">{value}</span></div>;
}
